import { Directive, ElementRef, inject, OnInit, OnDestroy, output, signal, DestroyRef } from '@angular/core';
import { fromEvent, debounceTime } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { VIEWPORT_CONFIG, WIDGET_DIMENSIONS, FALLBACKS } from '../constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface ViewportDimensions {
  height: number;
  width: number;
  containerHeight: number;
  isVisible: boolean;
  availableHeight: number;
  containerTop: number;
  isSticky: boolean;
  hasScrolled: boolean;
}

@Directive({
  selector: '[olViewportAware]',
})
export class ViewportAwareDirective implements OnInit, OnDestroy {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly document = inject(DOCUMENT);

  private readonly viewportHeight = signal(0);
  private readonly containerHeight = signal(0);
  private readonly hasScrolled = signal(false);
  private readonly initialTop = signal<number | null>(null);

  // Outputs
  readonly viewportChange = output<ViewportDimensions>();
  readonly visibilityChange = output<boolean>();

  private resizeObserver?: ResizeObserver;
  private intersectionObserver?: IntersectionObserver;

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.initializeObservers();
    this.setupResizeListener();

    setTimeout(() => {
      const rect = this.elementRef.nativeElement.getBoundingClientRect();
      this.initialTop.set(rect.top);
      this.updateDimensions();
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
  }

  refresh(): void {
    this.updateDimensions();
  }

  private initializeObservers(): void {
    const element = this.elementRef.nativeElement;

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          this.containerHeight.set(entry.contentRect.height);
          this.updateDimensions();
        }
      });
      this.resizeObserver.observe(element);
    }

    if (typeof IntersectionObserver !== 'undefined') {
      this.intersectionObserver = new IntersectionObserver(
        entries => {
          for (const entry of entries) {
            this.visibilityChange.emit(entry.isIntersecting);
            if (entry.isIntersecting) {
              this.updateDimensions();
            }
          }
        },
        {
          rootMargin: VIEWPORT_CONFIG.ROOT_MARGIN,
          threshold: [...VIEWPORT_CONFIG.INTERSECTION_THRESHOLDS],
        }
      );
      this.intersectionObserver.observe(element);
    }
  }

  private setupResizeListener(): void {
    const window = this.document.defaultView;
    if (!window) return;

    fromEvent(window, 'resize')
      .pipe(debounceTime(VIEWPORT_CONFIG.RESIZE_DEBOUNCE), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.viewportHeight.set(window.innerHeight);
        this.updateDimensions();
      });

    fromEvent(window, 'scroll', { passive: true })
      .pipe(debounceTime(VIEWPORT_CONFIG.SCROLL_DEBOUNCE), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.hasScrolled.set(true);
        this.updateDimensions();
      });

    this.viewportHeight.set(window.innerHeight);
  }

  private updateDimensions(): void {
    const element = this.elementRef.nativeElement;
    const rect = element.getBoundingClientRect();
    const viewportH = this.viewportHeight();
    const initialTop = this.initialTop();
    const hasScrolled = this.hasScrolled();

    // check if element is sticky
    const computedStyle = this.document.defaultView?.getComputedStyle(element);
    const position = computedStyle?.position;
    const isSticky = position === 'sticky';

    // calculate container top
    const containerTop = rect.top;
    const isStickyActive =
      isSticky &&
      initialTop !== null &&
      Math.abs(containerTop - initialTop) > VIEWPORT_CONFIG.STICKY_POSITION_THRESHOLD;

    // calculate available height
    const bottomPadding = WIDGET_DIMENSIONS.BOTTOM_MARGIN;
    const availableHeight = Math.max(0, viewportH - containerTop - bottomPadding);
    const isVisible = containerTop < viewportH && rect.bottom > 0;

    const dimensions: ViewportDimensions = {
      height: viewportH,
      width: this.document.defaultView?.innerWidth ?? FALLBACKS.VIEWPORT_WIDTH,
      containerHeight: element.offsetHeight,
      isVisible,
      availableHeight,
      containerTop,
      isSticky: isStickyActive,
      hasScrolled,
    };

    this.viewportChange.emit(dimensions);
  }
}
